import styled from '@emotion/styled';
import { useAnnouncementContext } from '@vmos2/providers';
import type { MutableRefObject } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type ErrorReason = {
  code: string,
  lastTimeUsed?: number,
  limitUsageMinutes?: number,
  minRequired?: number,
};

type LoyaltyPromotionErrorProps = {
  errorReasons?: ErrorReason[],
  errorRef?: MutableRefObject<HTMLSpanElement | null>,
  isMissingItems?: boolean,
};

const remainingTimeFromMinutes = (
  t: (translation: string, options: Record<string, number | string>) => string,
  remainingTime: number
) => {
  const hours = Math.floor(remainingTime / 60);
  let minutes: number | string = Math.round(remainingTime % 60);

  minutes = minutes < 10 ? `0${minutes}` : minutes;
  minutes = t('checkout.discounts:minutes', { count: minutes });

  return hours > 0 ? `${hours}h${minutes}` : `${minutes}`;
};

const LoyaltyPromotionError = ({ errorReasons, errorRef, isMissingItems = false }: LoyaltyPromotionErrorProps) => {
  const { addAnnouncement } = useAnnouncementContext();
  const { t } = useTranslation('');
  const errorReason = errorReasons?.[0];

  const errorMessage = (() => {
    if (errorReason) {
      const { code, minRequired, lastTimeUsed = 0, limitUsageMinutes = 0 } = errorReason;
      const remainingTime = remainingTimeFromMinutes(t, limitUsageMinutes - lastTimeUsed);

      return t(`checkout.discounts:${code}`, { minRequired, remainingTime });
    }

    if (isMissingItems) {
      return t('checkout.discounts:noEligibleItems');
    }

    return null;
  })();

  useEffect(() => {
    if (errorMessage) {
      addAnnouncement(errorMessage);
    }
  }, [addAnnouncement, errorMessage]);

  return (
    errorMessage && (
      <Error aria-hidden="true" data-test="loyalty-promotion-error" ref={errorRef}>
        {errorMessage}
      </Error>
    )
  );
};

const Error = styled.span(
  ({ theme }) => `
    display: block;
    margin-top: ${theme.margins.xs};
    color: ${theme.color.danger};
    font-size: ${theme.fontSize.sm};
    font-weight: ${theme.fontWeight.bold};
  `
);

export type { LoyaltyPromotionErrorProps };
export { LoyaltyPromotionError };
